import { useState, useEffect } from 'react';
import { DashboardLayout } from '../components/DashboardLayout';
import { clientsAPI } from '../lib/api';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  UsersThree, Lightning, CircleNotch, FileText, TrendUp, Eye, ChatCircle
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('nx_token')}` } });
const TIER_COLORS = { nano: 'nx-tag-gray', micro: 'nx-tag-blue', macro: 'nx-tag-green' };

export default function CreatorsPage() {
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState('');
  const [creators, setCreators] = useState([]);
  const [briefs, setBriefs] = useState([]);
  const [citationReport, setCitationReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [genBrief, setGenBrief] = useState(false);
  const [briefTier, setBriefTier] = useState('micro');

  useEffect(() => {
    clientsAPI.list().then(r => { setClients(r.data); if (r.data.length > 0) setSelectedClient(r.data[0].id); })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!selectedClient) return;
    Promise.all([
      axios.get(`${API}/creators/${selectedClient}`, auth()),
      axios.get(`${API}/creators/briefs/${selectedClient}`, auth()),
      axios.get(`${API}/creators/citation-report/${selectedClient}`, auth()),
    ]).then(([c, b, r]) => {
      setCreators(c.data);
      setBriefs(b.data);
      setCitationReport(r.data);
    }).catch(() => {});
  }, [selectedClient]);

  const handleGenBrief = async () => {
    setGenBrief(true);
    try {
      const res = await axios.post(`${API}/creators/brief`, { client_id: selectedClient, creator_tier: briefTier, language: 'en' }, auth());
      setBriefs(prev => [res.data, ...prev]);
      toast.success('Creator brief generated');
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setGenBrief(false); }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="creators-page">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <h1 className="font-display text-2xl sm:text-3xl font-bold text-[var(--text-primary)] tracking-tight">Creator Seeding Agent</h1>
            <p className="text-sm text-[var(--text-secondary)] mt-1">Micro/nano influencer SEO layer — convert creator content into LLM-citable inventory</p>
          </div>
          <Select value={selectedClient} onValueChange={setSelectedClient}>
            <SelectTrigger className="w-48 bg-[var(--surface-sunken)] border-[var(--border)] text-[var(--text-primary)]"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-[var(--surface)] border-[var(--border)]">
              {clients.map(c => <SelectItem key={c.id} value={c.id} className="text-[var(--text-primary)] hover:bg-[var(--surface-sunken)]">{c.display_name || c.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        {/* Citation Summary */}
        {citationReport && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="nx-metric-card rounded-lg">
              <p className="text-xs text-[var(--text-tertiary)] font-mono uppercase tracking-wider mb-2">Creators</p>
              <p className="text-2xl font-bold text-[var(--text-primary)] font-mono">{citationReport.creators?.length || 0}</p>
            </div>
            <div className="nx-metric-card rounded-lg">
              <p className="text-xs text-[var(--text-tertiary)] font-mono uppercase tracking-wider mb-2">LLM Citations</p>
              <p className="text-2xl font-bold text-[var(--accent)] font-mono">{citationReport.total_citations}</p>
            </div>
            <div className="nx-metric-card rounded-lg">
              <p className="text-xs text-[var(--text-tertiary)] font-mono uppercase tracking-wider mb-2">SoM Contribution</p>
              <p className="text-2xl font-bold text-[var(--success)] font-mono">{citationReport.som_contribution_total}%</p>
            </div>
            <div className="nx-metric-card rounded-lg">
              <p className="text-xs text-[var(--text-tertiary)] font-mono uppercase tracking-wider mb-2">Briefs Generated</p>
              <p className="text-2xl font-bold text-[var(--warning)] font-mono">{briefs.length}</p>
            </div>
          </div>
        )}

        <Tabs defaultValue="creators" className="space-y-4">
          <TabsList className="bg-[var(--surface)] border border-[var(--border)]">
            <TabsTrigger value="creators" className="data-[state=active]:bg-[var(--accent)] data-[state=active]:text-white">
              <UsersThree size={14} className="mr-1.5" /> Creators ({creators.length})
            </TabsTrigger>
            <TabsTrigger value="briefs" className="data-[state=active]:bg-[var(--accent)] data-[state=active]:text-white">
              <FileText size={14} className="mr-1.5" /> Briefs ({briefs.length})
            </TabsTrigger>
            <TabsTrigger value="citations" className="data-[state=active]:bg-[var(--accent)] data-[state=active]:text-white">
              <TrendUp size={14} className="mr-1.5" /> Citation Report
            </TabsTrigger>
          </TabsList>

          <TabsContent value="creators" className="space-y-4">
            <div className="nx-metric-card rounded-lg" data-testid="creators-table">
              <table className="nx-table">
                <thead><tr><th>Creator</th><th>Platform</th><th>Tier</th><th>Followers</th><th>Engagement</th><th>Language</th><th>Content</th></tr></thead>
                <tbody>
                  {creators.map(c => (
                    <tr key={c.id}>
                      <td>
                        <div>
                          <p className="text-[var(--text-primary)] text-sm font-medium">{c.name}</p>
                          <p className="text-[10px] text-[var(--text-tertiary)] font-mono">{c.handle}</p>
                        </div>
                      </td>
                      <td><span className="nx-tag nx-tag-blue">{c.platform}</span></td>
                      <td><span className={`nx-tag ${TIER_COLORS[c.tier]}`}>{c.tier}</span></td>
                      <td className="font-mono">{c.followers?.toLocaleString()}</td>
                      <td className={`font-mono ${c.engagement_rate >= 5 ? 'text-[var(--success)]' : 'text-[var(--warning)]'}`}>{c.engagement_rate}%</td>
                      <td className="text-xs">{c.language}</td>
                      <td className="font-mono">{c.content_pieces}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>

          <TabsContent value="briefs" className="space-y-4">
            <div className="flex items-center gap-3 mb-2">
              <Select value={briefTier} onValueChange={setBriefTier}>
                <SelectTrigger className="w-32 bg-[var(--surface-sunken)] border-[var(--border)] text-[var(--text-primary)]"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[var(--surface)] border-[var(--border)]">
                  <SelectItem value="nano" className="text-[var(--text-primary)] hover:bg-[var(--surface-sunken)]">Nano (1-10K)</SelectItem>
                  <SelectItem value="micro" className="text-[var(--text-primary)] hover:bg-[var(--surface-sunken)]">Micro (10-50K)</SelectItem>
                  <SelectItem value="macro" className="text-[var(--text-primary)] hover:bg-[var(--surface-sunken)]">Macro (50K+)</SelectItem>
                </SelectContent>
              </Select>
              <Button onClick={handleGenBrief} disabled={genBrief} className="bg-[var(--accent)] hover:bg-[var(--accent-hover)] text-white">
                {genBrief ? <CircleNotch size={14} className="animate-spin mr-2" /> : <FileText size={14} className="mr-2" />}
                Generate Brief
              </Button>
            </div>
            {briefs.length === 0 ? (
              <div className="nx-metric-card rounded-lg text-center py-12">
                <FileText size={40} className="mx-auto mb-3 text-[var(--text-tertiary)]" />
                <p className="text-[var(--text-secondary)]">No briefs yet. Generate one to get SEO-optimized creator instructions.</p>
              </div>
            ) : (
              briefs.map(b => (
                <div key={b.id} className="nx-metric-card rounded-lg animate-fade-in-up" data-testid={`brief-${b.id}`}>
                  <div className="flex items-center gap-2 mb-3">
                    <span className={`nx-tag ${TIER_COLORS[b.creator_tier]}`}>{b.creator_tier}</span>
                    <span className="text-xs text-[var(--text-tertiary)] font-mono">{b.language} &middot; {new Date(b.created_at).toLocaleDateString()}</span>
                  </div>
                  {b.brief && typeof b.brief === 'object' && !b.brief.raw ? (
                    <div className="space-y-2">
                      {b.brief.title && <p className="text-sm text-[var(--text-primary)] font-bold">{b.brief.title}</p>}
                      {b.brief.objective && <p className="text-xs text-[var(--text-secondary)]">{b.brief.objective}</p>}
                      {b.brief.talking_points?.length > 0 && (
                        <div><p className="text-[10px] text-[var(--text-tertiary)] font-mono uppercase mb-1">Talking Points</p>
                          <ul className="space-y-1">{b.brief.talking_points.map((tp, i) => <li key={i} className="text-xs text-[var(--text-secondary)]">- {tp}</li>)}</ul>
                        </div>
                      )}
                      {b.brief.seo_keywords?.length > 0 && (
                        <div className="flex flex-wrap gap-1">{b.brief.seo_keywords.map((kw, i) => <span key={i} className="nx-tag nx-tag-blue text-[10px]">{kw}</span>)}</div>
                      )}
                    </div>
                  ) : (
                    <pre className="text-xs text-[var(--text-secondary)] bg-[var(--surface-sunken)] p-3 rounded overflow-x-auto max-h-40 font-mono">{JSON.stringify(b.brief, null, 2)}</pre>
                  )}
                </div>
              ))
            )}
          </TabsContent>

          <TabsContent value="citations" className="space-y-4">
            {citationReport && (
              <div className="nx-metric-card rounded-lg" data-testid="citation-table">
                <h3 className="font-display text-sm font-bold text-[var(--text-primary)] mb-4">Creator Citation Report — LLM Contribution to SoM</h3>
                <table className="nx-table">
                  <thead><tr><th>Creator</th><th>Platform</th><th>LLM Crawled</th><th>Citations</th><th>SoM Contrib.</th><th>Indexed</th></tr></thead>
                  <tbody>
                    {citationReport.creators?.map(c => (
                      <tr key={c.id}>
                        <td className="!text-[var(--text-primary)]">{c.name}</td>
                        <td><span className="nx-tag nx-tag-blue">{c.platform}</span></td>
                        <td>{c.llm_crawled ? <Eye size={14} className="text-[var(--success)]" weight="fill" /> : <Eye size={14} className="text-[var(--text-tertiary)]" />}</td>
                        <td className="font-mono text-[var(--accent)]">{c.llm_citations}</td>
                        <td className="font-mono font-bold text-[var(--success)]">{c.som_contribution}%</td>
                        <td className="font-mono">{c.platforms_indexed}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
}
